// Shared bits for the Cloud surface (hosted apps, databases, servers, network).
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import {
  Camera,
  ExternalLink,
  Eye,
  Loader2,
  MoreHorizontal,
  Power,
  PowerOff,
  RefreshCw,
  Rocket,
  RotateCw,
  ScrollText,
  Zap,
} from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  cloudKeys,
  useCancelDeploymentMutation,
  useDeployAppMutation,
  useDropletActionMutation,
  useRestartAppMutation,
  useRollbackAppMutation,
  useScaleComponentMutation,
} from '@/client/api/cloud/cloud.query';
import { getApiErrorCode, getApiErrorField, getApiErrorMessage } from '@/client/utils/errors';
import {
  CloudAuditRow,
  CloudResource,
  Deployment,
  DropletAction,
  Health,
  HealthLevel,
  HealthTally,
  MetricSeries,
} from '@/client/types/cloud';
import {
  Card,
  EmptyRow,
  INPUT_CLASS,
  ReasonDialog,
  apiStatus,
  fmtTime,
} from '../Scheduler/schedulerUi';

export {
  Card,
  EmptyRow,
  Field,
  INPUT_CLASS,
  PageShell,
  ReasonDialog,
  RunsChart,
  StatCard,
  Th,
  fmtAgo,
  fmtDur,
  fmtNum,
  fmtRate,
  fmtTime,
} from '../Scheduler/schedulerUi';

export const CLOUD_BASE = '/admin/cloud';

/* ---------- health: red, yellow, green ---------- */

// Colour is never the only signal: every dot carries its word.
export const HEALTH: Record<
  HealthLevel,
  { label: string; dot: string; pill: string; text: string; border: string }
> = {
  red: {
    label: 'Critical',
    dot: 'bg-red-500',
    pill: 'bg-red-50 text-red-700 ring-red-200',
    text: 'text-red-700',
    border: 'border-l-red-500',
  },
  yellow: {
    label: 'Warning',
    dot: 'bg-amber-400',
    pill: 'bg-amber-50 text-amber-800 ring-amber-200',
    text: 'text-amber-800',
    border: 'border-l-amber-400',
  },
  green: {
    label: 'Healthy',
    dot: 'bg-green-500',
    pill: 'bg-green-50 text-green-700 ring-green-200',
    text: 'text-green-700',
    border: 'border-l-green-500',
  },
  grey: {
    label: 'Not monitored',
    dot: 'bg-gray-300',
    pill: 'bg-gray-100 text-gray-600 ring-gray-200',
    text: 'text-gray-500',
    border: 'border-l-gray-200',
  },
};

export const LEVELS: HealthLevel[] = ['red', 'yellow', 'green', 'grey'];

export const HealthDot: React.FC<{ level: HealthLevel; className?: string }> = ({
  level,
  className = '',
}) => (
  <span
    role="img"
    aria-label={HEALTH[level].label}
    title={HEALTH[level].label}
    className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${HEALTH[level].dot} ${className}`}
  />
);

/**
 * Red, yellow and green are pills. A resource nothing judges is not a state
 * worth a pill: it gets a quiet marker, so the colours stay the things to read.
 */
export const HealthPill: React.FC<{ level: HealthLevel }> = ({ level }) =>
  level === 'grey' ? (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-gray-400">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
      {HEALTH.grey.label}
    </span>
  ) : (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${HEALTH[level].pill}`}
    >
      <span className={`h-2 w-2 rounded-full ${HEALTH[level].dot}`} />
      {HEALTH[level].label}
    </span>
  );

/** Why a resource is the colour it is. Green with nothing to say renders nothing. */
export const HealthReasons: React.FC<{ health: Health; className?: string }> = ({
  health,
  className = '',
}) =>
  health.reasons.length ? (
    <ul className={`space-y-0.5 text-xs ${className}`}>
      {health.reasons.map((r) => (
        <li key={r.code} className={HEALTH[r.level].text}>
          {r.text}
        </li>
      ))}
    </ul>
  ) : null;

/** The three counts that answer "is anything wrong", each a filter when clickable. */
export const HealthSummary: React.FC<{
  tally?: HealthTally;
  active?: HealthLevel | '';
  onPick?: (level: HealthLevel | '') => void;
}> = ({ tally, active = '', onPick }) => (
  <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
    {LEVELS.map((level) => {
      const on = active === level;
      const body = (
        <>
          <span className={`h-3.5 w-3.5 rounded-full ${HEALTH[level].dot}`} />
          <span className="text-2xl font-semibold text-gray-900">{tally ? tally[level] : '—'}</span>
          <span className="text-sm text-gray-600">{HEALTH[level].label}</span>
        </>
      );
      const cls = `flex items-center gap-3 rounded-xl border border-l-4 bg-white px-4 py-3 shadow-sm ${
        HEALTH[level].border
      } ${on ? 'border-gray-400 ring-1 ring-gray-300' : 'border-gray-100'}`;
      return onPick ? (
        <button
          key={level}
          type="button"
          aria-pressed={on}
          onClick={() => onPick(on ? '' : level)}
          className={`${cls} text-left hover:bg-gray-50`}
        >
          {body}
        </button>
      ) : (
        <div key={level} className={cls}>
          {body}
        </div>
      );
    })}
  </div>
);

/* ---------- small pieces ---------- */

export const Tabs = <T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: readonly T[];
}) => (
  <div className="inline-flex overflow-hidden rounded-lg border border-gray-200">
    {options.map((o) => (
      <button
        key={o}
        type="button"
        onClick={() => onChange(o)}
        className={`px-3 py-1.5 text-xs font-medium ${
          value === o ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
      >
        {o}
      </button>
    ))}
  </div>
);

export const EnvBadge: React.FC<{ env?: string }> = ({ env }) =>
  env ? (
    <span
      className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
        env === 'production' ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {env}
    </span>
  ) : null;

const PHASE_STYLE: Record<string, string> = {
  ACTIVE: 'bg-green-50 text-green-700',
  SUPERSEDED: 'bg-gray-100 text-gray-600',
  ERROR: 'bg-red-50 text-red-700',
  CANCELED: 'bg-gray-100 text-gray-500',
};

export const isDeploying = (phase?: string) =>
  !!phase && ['PENDING_BUILD', 'BUILDING', 'PENDING_DEPLOY', 'DEPLOYING'].includes(phase);

export const PhaseBadge: React.FC<{ phase: string }> = ({ phase }) => (
  <span
    className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${
      PHASE_STYLE[phase] ?? 'bg-blue-50 text-blue-700'
    }`}
  >
    {phase.toLowerCase().replace(/_/g, ' ')}
  </span>
);

/** What set a deployment off, in the fewest words that identify it. */
export const deploymentCause = (d: Deployment) =>
  d.commit ? `${d.commit.slice(0, 7)} ${d.commit_message ?? ''}`.trim() : (d.cause ?? '—');

export const AppLink: React.FC<{ id: string; name: string; className?: string }> = ({
  id,
  name,
  className = 'font-medium text-gray-900 hover:underline',
}) => (
  <Link to={`${CLOUD_BASE}/apps/${encodeURIComponent(id)}`} className={className}>
    {name}
  </Link>
);

/** A resource's name, linked to its page when it has one. */
export const ResourceName: React.FC<{ resource: Pick<CloudResource, 'kind' | 'id' | 'name'> }> = ({
  resource,
}) => {
  const page = detailPath(resource);
  return page ? (
    <Link to={page} className="font-medium text-gray-900 hover:underline">
      {resource.name}
    </Link>
  ) : (
    <span className="font-medium text-gray-900">{resource.name}</span>
  );
};

export const fmtPct = (n?: number | null) => (n == null ? '—' : `${Math.round(n)}%`);

/** A usage figure coloured by the same thresholds the server judges health on. */
export const UsagePct: React.FC<{ pct?: number | null }> = ({ pct }) => (
  <span
    className={
      pct == null
        ? 'text-gray-400'
        : pct >= 95
          ? 'font-medium text-red-700'
          : pct >= 85
            ? 'font-medium text-amber-700'
            : 'text-gray-700'
    }
  >
    {fmtPct(pct)}
  </span>
);

export const KIND_LABEL: Record<string, string> = {
  app: 'App',
  database: 'Database',
  server: 'Droplet',
  kubernetes: 'Kubernetes cluster',
  domain: 'Domain',
  cdn: 'CDN endpoint',
  firewall: 'Firewall',
  vpc: 'VPC',
  load_balancer: 'Load balancer',
  reserved_ip: 'Reserved IP',
  volume: 'Volume',
  snapshot: 'Snapshot',
  space: 'Space',
};

export const ACTION_LABEL: Record<string, string> = {
  deploy: 'Deployed',
  restart: 'Restarted',
  cancel_deployment: 'Cancelled a deployment',
  rollback: 'Rolled back',
  scale: 'Scaled',
  ack_issue: 'Acknowledged an issue',
  reboot: 'Rebooted',
  power_on: 'Powered on',
  snapshot: 'Took a snapshot',
  power_cycle: 'Power cycled',
  shutdown: 'Shut down',
  env: 'Changed variables',
  resize: 'Resized',
  trust_add: 'Allowed a source',
  trust_remove: 'Stopped allowing a source',
  dns_create: 'Added a DNS record',
  dns_update: 'Changed a DNS record',
  dns_delete: 'Deleted a DNS record',
};

/** What an audit row changed, in a few words. Variable names only; a value is never held. */
export const auditDetail = (r: CloudAuditRow): string => {
  if (r.action === 'scale') return `${r.component}: ${r.from} to ${r.to}`;
  if (r.action === 'resize') return `${r.from} to ${r.to}`;
  if (r.action === 'env') {
    const parts = [
      r.added?.length ? `added ${r.added.join(', ')}` : '',
      r.replaced?.length ? `replaced ${r.replaced.join(', ')}` : '',
      r.removed?.length ? `removed ${r.removed.join(', ')}` : '',
    ].filter(Boolean);
    return `${r.target === 'app' ? 'app level' : r.target}: ${parts.join('; ')}`;
  }
  if (r.action === 'trust_add' || r.action === 'trust_remove')
    return `${r.source?.type ?? ''} ${r.source?.value ?? ''}`.trim();
  if (r.action.startsWith('dns_')) {
    const rec = r.after ?? r.before;
    return rec ? `${rec.type} ${rec.name} ${rec.data}` : '';
  }
  return '';
};

/** The console's own history for one resource. */
export const AuditCard: React.FC<{ rows: CloudAuditRow[]; empty: string }> = ({ rows, empty }) => (
  <Card title="Console history" flush>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <tbody>
          {rows.length === 0 && <EmptyRow cols={4}>{empty}</EmptyRow>}
          {rows.map((r, i) => (
            <tr key={`${r.at}-${i}`} className="border-b border-gray-50 align-top last:border-0">
              <td className="whitespace-nowrap px-4 py-2 text-gray-500">{fmtTime(r.at)}</td>
              <td className="whitespace-nowrap px-4 py-2 text-gray-700">{r.by}</td>
              <td className="px-4 py-2 text-gray-900">
                {ACTION_LABEL[r.action] ?? r.action}
                <span className="block break-all text-xs text-gray-500">{auditDetail(r)}</span>
              </td>
              <td className="px-4 py-2 text-gray-600">{r.reason || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

/** The page a resource has, if it has one. The rest open a details panel. */
export const detailPath = (r: Pick<CloudResource, 'kind' | 'id' | 'name'>): string | null => {
  const id = encodeURIComponent(r.id);
  if (r.kind === 'app') return `${CLOUD_BASE}/apps/${id}`;
  if (r.kind === 'server') return `${CLOUD_BASE}/droplets/${id}`;
  if (r.kind === 'database') return `${CLOUD_BASE}/databases/${id}`;
  if (r.kind === 'domain') return `${CLOUD_BASE}/domains/${encodeURIComponent(r.name)}`;
  return null;
};

/**
 * A change that needs more than a reason: its own fields, then the reason,
 * and for anything that can take a service away the resource's name typed out.
 */
export const FormDialog: React.FC<{
  title: string;
  description?: React.ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  confirmText?: string;
  valid?: boolean;
  busy?: boolean;
  error?: unknown;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  children?: React.ReactNode;
}> = ({
  title,
  description,
  confirmLabel,
  destructive,
  confirmText,
  valid = true,
  busy,
  error,
  onClose,
  onConfirm,
  children,
}) => {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const ready = valid && reason.trim().length >= 5 && (!confirmText || typed === confirmText);
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          {children}
          {confirmText && (
            <label className="block text-sm">
              <span className="text-gray-700">
                Type <span className="font-mono font-medium">{confirmText}</span> to confirm
              </span>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className={`${INPUT_CLASS} mt-1 w-full`}
              />
            </label>
          )}
          <label className="block text-sm">
            <span className="text-gray-700">Reason (at least 5 characters)</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-blue-400"
            />
          </label>
          {!!error && <p className="text-sm text-red-600">{cloudErrorText(error)}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            disabled={busy || !ready}
            onClick={() => onConfirm(reason.trim())}
          >
            {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** A label over a form control, for the dialogs above. */
export const FormField: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({
  label,
  hint,
  children,
}) => (
  <label className="block text-sm">
    <span className="text-gray-700">{label}</span>
    {children}
    {hint && <span className="mt-0.5 block text-xs text-gray-500">{hint}</span>}
  </label>
);

/* ---------- errors ---------- */

const ERROR_COPY: Record<string, string> = {
  cloud_unreachable: 'The cloud provider could not be reached.',
  cloud_timeout: 'The cloud provider took too long to answer.',
  cloud_key_rejected: 'The cloud provider rejected our API token.',
  cloud_rate_limited: 'The cloud provider is rate limiting us. Try again in a minute.',
  missing_reason: 'A reason is required.',
  rollback_refused: 'The provider refused this rollback.',
  scale_refused: 'This change was refused.',
  env_refused: 'The variables were not changed.',
  resize_refused: 'The database was not resized.',
  trusted_source_refused: 'The trusted source was not changed.',
  confirmation_required: 'This change has a consequence that must be confirmed first.',
  invalid_fields: 'Some fields are not valid.',
};

export const cloudErrorText = (error: unknown): string => {
  const code = getApiErrorCode(error);
  const detail = getApiErrorField<string>(error, 'detail');
  const base = (code && ERROR_COPY[code]) || getApiErrorMessage(error, 'Something went wrong');
  const fields = getApiErrorField<Record<string, string>>(error, 'fields');
  const named =
    fields && typeof fields === 'object'
      ? Object.entries(fields)
          .map(([field, message]) => `${field}: ${message}`)
          .join('; ')
      : '';
  return [base, typeof detail === 'string' ? detail : '', named].filter(Boolean).join(' ');
};

export const ErrorNotice: React.FC<{ error: unknown; onRetry?: () => void }> = ({
  error,
  onRetry,
}) =>
  apiStatus(error) === 503 && getApiErrorCode(error) === 'cloud_not_configured' ? (
    <div className="rounded-xl border border-amber-100 bg-amber-50 p-10 text-center shadow-sm">
      <p className="text-sm font-medium text-amber-900">Cloud provider not connected</p>
      <p className="mt-1 text-sm text-amber-800">
        DO_API_TOKEN has not been set for the admin API.
      </p>
    </div>
  ) : (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
      <span>{cloudErrorText(error)}</span>
      {onRetry && (
        <Button size="sm" variant="outline" onClick={onRetry}>
          <RefreshCw className="mr-2 h-3.5 w-3.5" />
          Retry
        </Button>
      )}
    </div>
  );

export const AccessDenied: React.FC<{ code?: string }> = ({ code = 'cloud.view' }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-10 text-center shadow-sm">
    <p className="text-sm font-medium text-gray-800">You don&apos;t have access to Cloud</p>
    <p className="mt-1 text-sm text-gray-500">Ask an administrator for the {code} permission.</p>
  </div>
);

/* ---------- chart ---------- */

const LINE_COLORS = ['#2563eb', '#0d9488', '#7c3aed', '#db2777', '#ea580c', '#4b5563'];

/** One line per running instance. `percent` pins the axis to 0-100 and marks 85%. */
export const MetricChart: React.FC<{
  series: MetricSeries[] | null;
  percent?: boolean;
  spanHours: number;
}> = ({ series, percent, spanHours }) => {
  if (series === null) {
    return <p className="py-10 text-center text-sm text-gray-400">This metric is unavailable.</p>;
  }
  const byTime = new Map<number, Record<string, number>>();
  series.forEach((s) =>
    s.points.forEach(([at, value]) => {
      byTime.set(at, { ...(byTime.get(at) ?? { at }), [s.instance]: value });
    }),
  );
  const data = [...byTime.values()].sort((a, b) => a.at - b.at);
  if (!data.length) {
    return <p className="py-10 text-center text-sm text-gray-400">No samples in this window.</p>;
  }
  const label = (at: number) =>
    spanHours <= 24
      ? new Date(at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : new Date(at * 1000).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit' });
  return (
    <div className="h-48 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
          <XAxis
            dataKey="at"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={label}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: '#9ca3af' }}
          />
          <YAxis
            width={36}
            domain={percent ? [0, 100] : [0, 'auto']}
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: '#9ca3af' }}
          />
          <Tooltip
            labelFormatter={(at) => new Date(Number(at) * 1000).toLocaleString()}
            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
          />
          {percent && <ReferenceLine y={85} stroke="#f59e0b" strokeDasharray="4 4" />}
          {series.map((s, i) => (
            <Line
              key={s.instance}
              dataKey={s.instance}
              type="monotone"
              dot={false}
              strokeWidth={1.5}
              stroke={LINE_COLORS[i % LINE_COLORS.length]}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ---------- loading ---------- */

/** Says what is being waited for. The first read of the account is slow enough to need it. */
export const LoadingNote: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div
    role="status"
    className="flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800"
  >
    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
    <span>
      {children ??
        'Reading the DigitalOcean account. The first read takes about 15 seconds; after that it is cached for five minutes.'}
    </span>
  </div>
);

/** Shows while any cloud read is in flight, including background refreshes. */
export const FetchingChip: React.FC = () =>
  useIsFetching({ queryKey: cloudKeys.all }) > 0 ? (
    <span
      role="status"
      className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
    >
      <Loader2 className="h-3 w-3 animate-spin" />
      Updating
    </span>
  ) : null;

/* ---------- sparkline ---------- */

/** A trend in a table cell. The dashed line is the 85% warning mark. */
export const Sparkline: React.FC<{ points?: [number, number][]; label: string }> = ({
  points,
  label,
}) => {
  if (!points || points.length < 2) return <span className="text-xs text-gray-400">no data</span>;
  const w = 120;
  const h = 28;
  const t0 = points[0][0];
  const span = points[points.length - 1][0] - t0 || 1;
  const peak = Math.max(...points.map((p) => p[1]));
  const y = (v: number) => h - 1 - (Math.min(v, 100) / 100) * (h - 2);
  const path = points.map(([t, v]) => `${(((t - t0) / span) * w).toFixed(1)},${y(v).toFixed(1)}`);
  const stroke = peak >= 95 ? '#dc2626' : peak >= 85 ? '#d97706' : '#2563eb';
  return (
    <svg width={w} height={h} role="img" aria-label={`${label}, peak ${Math.round(peak)}%`}>
      <line x1={0} x2={w} y1={y(85)} y2={y(85)} stroke="#e5e7eb" strokeDasharray="3 3" />
      <polyline points={path.join(' ')} fill="none" stroke={stroke} strokeWidth={1.5} />
    </svg>
  );
};

/* ---------- where it lives at the provider ---------- */

const DO_CONSOLE = 'https://cloud.digitalocean.com';

/** Where this resource lives in the provider's own console. */
export const consoleUrl = (r: Pick<CloudResource, 'kind' | 'id' | 'name'>): string => {
  const id = encodeURIComponent(r.id);
  const paths: Record<string, string> = {
    app: `/apps/${id}`,
    database: `/databases/${id}`,
    server: `/droplets/${id}`,
    kubernetes: `/kubernetes/clusters/${id}`,
    domain: `/networking/domains/${encodeURIComponent(r.name)}`,
    firewall: `/networking/firewalls/${id}`,
    vpc: `/networking/vpc/${id}`,
    load_balancer: `/networking/load_balancers/${id}`,
    reserved_ip: '/networking/reserved_ips',
    cdn: '/spaces',
    space: `/spaces/${encodeURIComponent(r.name)}`,
    volume: '/volumes',
    snapshot: '/images/snapshots/droplets',
  };
  return `${DO_CONSOLE}${paths[r.kind] ?? ''}`;
};

/* ---------- actions ---------- */

export type AppRef = Pick<CloudResource, 'id' | 'name' | 'environment'>;

export type Action =
  | { kind: 'deploy' }
  | { kind: 'restart'; component?: string }
  | { kind: 'cancel'; deployment: Deployment }
  | { kind: 'rollback'; deployment: Deployment }
  | { kind: 'scale'; component: string; from: number; to: number };

/** Every change to the app goes through one reasoned confirmation. */
export const ActionDialog: React.FC<{ app: AppRef; action: Action; onClose: () => void }> = ({
  app,
  action,
  onClose,
}) => {
  const deploy = useDeployAppMutation();
  const restart = useRestartAppMutation();
  const cancel = useCancelDeploymentMutation();
  const rollback = useRollbackAppMutation();
  const scale = useScaleComponentMutation();
  const done = { onSuccess: onClose };
  const live = app.environment === 'production';

  const spec = {
    deploy: {
      m: deploy,
      title: `Deploy ${app.name}`,
      text: 'Builds and releases the current head of its branch. The running release serves until the new one is healthy.',
      label: 'Deploy',
      run: (reason: string) => deploy.mutate({ appId: app.id, reason }, done),
    },
    restart: {
      m: restart,
      title:
        action.kind === 'restart' && action.component
          ? `Restart ${action.component}`
          : `Restart ${app.name}`,
      text: 'Replaces the running instances with fresh ones on the same release. Requests in flight on an instance are cut off.',
      label: 'Restart',
      run: (reason: string) =>
        restart.mutate(
          {
            appId: app.id,
            reason,
            components: action.kind === 'restart' && action.component ? [action.component] : [],
          },
          done,
        ),
    },
    cancel: {
      m: cancel,
      title: 'Cancel this deployment',
      text: 'Stops the deployment in progress. The running release keeps serving.',
      label: 'Cancel deployment',
      run: (reason: string) =>
        action.kind === 'cancel' &&
        cancel.mutate({ appId: app.id, deploymentId: action.deployment.id, reason }, done),
    },
    rollback: {
      m: rollback,
      title: `Roll ${app.name} back`,
      text:
        action.kind === 'rollback'
          ? `Releases the build from ${fmtTime(action.deployment.created_at)} (${deploymentCause(action.deployment)}) again. Database changes made since are not undone, and the next push deploys as usual.`
          : '',
      label: 'Roll back',
      run: (reason: string) =>
        action.kind === 'rollback' &&
        rollback.mutate({ appId: app.id, deploymentId: action.deployment.id, reason }, done),
    },
    scale: {
      m: scale,
      title: action.kind === 'scale' ? `Scale ${action.component}` : '',
      text:
        action.kind === 'scale'
          ? `Changes ${action.component} from ${action.from} to ${action.to} instances. This starts a deployment and changes what we are billed.`
          : '',
      label: 'Scale',
      run: (reason: string) =>
        action.kind === 'scale' &&
        scale.mutate(
          { appId: app.id, component: action.component, count: action.to, reason },
          done,
        ),
    },
  }[action.kind];

  const risky = action.kind === 'rollback' || action.kind === 'scale';
  return (
    <ReasonDialog
      open
      onClose={onClose}
      title={spec.title}
      description={spec.text}
      confirmLabel={spec.label}
      destructive={action.kind !== 'deploy'}
      minReason={5}
      // Production, or anything that changes what is released or billed, is typed out.
      confirmText={risky || (live && action.kind !== 'cancel') ? app.name : undefined}
      busy={spec.m.isPending}
      error={spec.m.error}
      onConfirm={spec.run}
    />
  );
};


const DROPLET_COPY: Record<DropletAction, { title: string; text: string; label: string }> = {
  reboot: {
    title: 'Reboot',
    text: 'Asks the operating system to restart. Everything running on it stops and comes back, usually within a minute or two.',
    label: 'Reboot',
  },
  power_on: { title: 'Power on', text: 'Starts the Droplet.', label: 'Power on' },
  snapshot: {
    title: 'Take a snapshot of',
    text: 'Copies the whole disk as it is now. The Droplet keeps running. Snapshots are billed for the space they take until deleted.',
    label: 'Take snapshot',
  },
  power_cycle: {
    title: 'Power cycle',
    text: 'Cuts the power and turns it back on, like pulling the plug. Use it only when a reboot does not work: anything not yet written to disk is lost.',
    label: 'Power cycle',
  },
  shutdown: {
    title: 'Shut down',
    text: 'Turns the Droplet off. Whatever it serves stays down until someone powers it on again, and it is still billed while off.',
    label: 'Shut down',
  },
};

/** Every Droplet action is confirmed by typing its name: these are single machines with no standby. */
const DropletActionDialog: React.FC<{
  droplet: Pick<CloudResource, 'id' | 'name'>;
  action: DropletAction;
  onClose: () => void;
}> = ({ droplet, action, onClose }) => {
  const act = useDropletActionMutation();
  const copy = DROPLET_COPY[action];
  return (
    <ReasonDialog
      open
      onClose={onClose}
      title={`${copy.title} ${droplet.name}`}
      description={copy.text}
      confirmLabel={copy.label}
      destructive={action !== 'snapshot' && action !== 'power_on'}
      minReason={5}
      confirmText={droplet.name}
      busy={act.isPending}
      error={act.error}
      onConfirm={(reason) =>
        act.mutate({ dropletId: droplet.id, action, reason }, { onSuccess: onClose })
      }
    />
  );
};

type MenuTarget = Pick<CloudResource, 'kind' | 'id' | 'name' | 'environment' | 'status'>;

const ITEM = 'cursor-pointer gap-2';
const DANGER = `${ITEM} text-red-600 focus:text-red-700`;

/**
 * The dotted menu at the end of a row: everything that can be done to that
 * resource from here. Anything that changes it opens a confirmation first.
 */
export const RowMenu: React.FC<{
  resource: MenuTarget;
  canOperate: boolean;
  canManage?: boolean;
  onDetails?: () => void;
}> = ({ resource: r, canOperate, canManage = false, onDetails }) => {
  const navigate = useNavigate();
  const [appAction, setAppAction] = useState<Action | null>(null);
  const [dropletAction, setDropletAction] = useState<DropletAction | null>(null);
  const appPath = `${CLOUD_BASE}/apps/${encodeURIComponent(r.id)}`;
  const isApp = r.kind === 'app';
  const isDroplet = r.kind === 'server';
  const ownPage = detailPath(r);
  return (
    <>
      {/* Not modal: a modal menu handing over to a dialog can leave the page unclickable. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" aria-label={`Actions for ${r.name}`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="truncate">{r.name}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {isApp ? (
            <>
              <DropdownMenuItem className={ITEM} onSelect={() => navigate(appPath)}>
                <Eye className="h-4 w-4" /> View details
              </DropdownMenuItem>
              <DropdownMenuItem className={ITEM} onSelect={() => navigate(`${appPath}#logs`)}>
                <ScrollText className="h-4 w-4" /> Logs
              </DropdownMenuItem>
            </>
          ) : (
            (ownPage || onDetails) && (
              <DropdownMenuItem
                className={ITEM}
                onSelect={() => (ownPage ? navigate(ownPage) : onDetails?.())}
              >
                <Eye className="h-4 w-4" /> View details
              </DropdownMenuItem>
            )
          )}
          {isApp && canOperate && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className={ITEM} onSelect={() => setAppAction({ kind: 'deploy' })}>
                <Rocket className="h-4 w-4" /> Deploy
              </DropdownMenuItem>
              <DropdownMenuItem className={ITEM} onSelect={() => setAppAction({ kind: 'restart' })}>
                <RotateCw className="h-4 w-4" /> Restart
              </DropdownMenuItem>
            </>
          )}
          {isDroplet && canOperate && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className={ITEM} onSelect={() => setDropletAction('snapshot')}>
                <Camera className="h-4 w-4" /> Take snapshot
              </DropdownMenuItem>
              {r.status === 'off' ? (
                <DropdownMenuItem className={ITEM} onSelect={() => setDropletAction('power_on')}>
                  <Power className="h-4 w-4" /> Power on
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem className={ITEM} onSelect={() => setDropletAction('reboot')}>
                  <RotateCw className="h-4 w-4" /> Reboot
                </DropdownMenuItem>
              )}
            </>
          )}
          {isDroplet && canManage && r.status !== 'off' && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className={DANGER} onSelect={() => setDropletAction('power_cycle')}>
                <Zap className="h-4 w-4" /> Power cycle
              </DropdownMenuItem>
              <DropdownMenuItem className={DANGER} onSelect={() => setDropletAction('shutdown')}>
                <PowerOff className="h-4 w-4" /> Shut down
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem className={ITEM} asChild>
            <a href={consoleUrl(r)} target="_blank" rel="noreferrer">
              <ExternalLink className="h-4 w-4" /> Open in DigitalOcean
            </a>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {appAction && <ActionDialog app={r} action={appAction} onClose={() => setAppAction(null)} />}
      {dropletAction && (
        <DropletActionDialog
          droplet={r}
          action={dropletAction}
          onClose={() => setDropletAction(null)}
        />
      )}
    </>
  );
};

/** The row menu for an app, where only an app is known (trends, issues). */
export const AppActions: React.FC<{ app: AppRef; canOperate: boolean }> = ({ app, canOperate }) => (
  <RowMenu resource={{ ...app, kind: 'app' }} canOperate={canOperate} />
);

/* ---------- details ---------- */

export const ConsoleLink: React.FC<{ resource: Pick<CloudResource, 'kind' | 'id' | 'name'> }> = ({
  resource,
}) => (
  <a
    href={consoleUrl(resource)}
    target="_blank"
    rel="noreferrer"
    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
  >
    Open in DigitalOcean <ExternalLink className="h-3.5 w-3.5" />
  </a>
);

/** Everything we know about a resource that has no page of its own. */
export const ResourceDialog: React.FC<{ resource: CloudResource; onClose: () => void }> = ({
  resource: r,
  onClose,
}) => {
  const gb = (mib?: number | null) => (mib ? `${Math.round(mib / 1024)} GB` : null);
  const rows: [string, React.ReactNode][] = [
    ['Type', KIND_LABEL[r.kind]],
    ['Filed under', r.category],
    ['Environment', r.environment],
    ['Status', r.status],
    ['Region', r.region],
    ['Engine', r.engine ? `${r.engine} ${r.version ?? ''}` : null],
    ['Size', r.size ? `${r.size}${r.nodes && r.nodes > 1 ? ` x ${r.nodes}` : ''}` : null],
    ['Storage', gb(r.storage_mib)],
    ['Maintenance window', r.maintenance?.day ? `${r.maintenance.day} ${r.maintenance.hour} UTC` : null],
    ['CPU', r.vcpus ? `${r.vcpus} vCPU` : null],
    ['Memory', r.memory_mb ? `${Math.round(r.memory_mb / 1024)} GB` : null],
    ['Disk', r.disk_gb ? `${r.disk_gb} GB` : null],
    ['Address', r.ip],
    ['Memory in use', r.metrics?.memory_pct != null ? <UsagePct pct={r.metrics.memory_pct} /> : null],
    ['Disk in use', r.metrics?.disk_pct != null ? <UsagePct pct={r.metrics.disk_pct} /> : null],
    ['Detail', r.detail],
    ['Created', r.created_at ? fmtTime(r.created_at) : null],
  ];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {r.name} <HealthPill level={r.health.level} />
          </DialogTitle>
          <DialogDescription>
            {r.health.reasons.length === 0 &&
              (r.health.level === 'grey'
                ? 'The provider reports no state for this kind of resource, so it is not judged.'
                : 'Nothing to report.')}
          </DialogDescription>
        </DialogHeader>
        <HealthReasons health={r.health} className="text-sm" />
        <dl className="grid grid-cols-[9rem_1fr] gap-x-4 gap-y-2 text-sm">
          {rows
            .filter(([, value]) => value != null && value !== '')
            .map(([label, value]) => (
              <React.Fragment key={label}>
                <dt className="text-gray-500">{label}</dt>
                <dd className="break-words text-gray-900">{value}</dd>
              </React.Fragment>
            ))}
        </dl>
        <ConsoleLink resource={r} />
      </DialogContent>
    </Dialog>
  );
};
